import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { AppDispatch } from "../common/redux/store";
import { useAppSelector } from "../common/redux/reduxHooks";
import {
    fetchNotifications,
    NotificationProps,
    readNotifications,
} from "../common/redux/notificationActions";
import { useTranslation } from "react-i18next";
import { DisponibiliteStructure } from "../structures/model/StructureModel";
import { RouteUtils } from "../utils/RouteUtils";

const ITEMS_PER_PAGE = 10;
const today = dayjs().format("YYYY-MM-DD");

export const useNotificationsLogic = (onClose: () => void) => {
    const [selectedStructure] = useState<DisponibiliteStructure | null>(null);

    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const notifications = useAppSelector((state) => state.notification.list);
    const role = useAppSelector((state) => state.auth.data.roles[1]);

    const { t } = useTranslation();

    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const loadNotifications = useCallback(
        async (nextPage: number, force = false) => {
            setLoading((prevLoading) => {
                if (prevLoading && !force) return true;

                (async () => {
                    const result = await dispatch(
                        fetchNotifications({
                            page: nextPage,
                            itemsPerPage: ITEMS_PER_PAGE,
                        })
                    ).unwrap();

                    if (nextPage === 1) setHasMore(true);
                    if (result.length < ITEMS_PER_PAGE) setHasMore(false);
                    setPage(nextPage);
                    setLoading(false);
                })();

                return true;
            });
        },
        [dispatch]
    );

    useEffect(() => {
        loadNotifications(1, true);

        const interval = setInterval(() => {
            loadNotifications(1, true);
        }, 120000);

        return () => clearInterval(interval);
    }, [role, loadNotifications]);

    const handleNavigateToProject = (url: string) => {
        const [, path] = url.split("://");
        navigate(`/${path}`);
        onClose();
    };

    const handleNotificationClick = async (notification: NotificationProps) => {
        dispatch(readNotifications(notification.id));

        const url = notification.data?.url;

        if (url?.includes("projects")) {
            const [, path] = url.split("://");
            const disponibiliteId = path.split("/")[1];
            navigate(
                `${RouteUtils.NOTIFICATION_DISPONIBILITE_STRUCTURE}/${disponibiliteId}`
            );
        } else if (url?.includes("missions")) {
            const [, path] = url.split("://");
            const missionId = path.split("/")[1];
            navigate(`${RouteUtils.NOTIFICATION_MISSION}/${missionId}`);
        }

        onClose();
    };

    const unreadCount = notifications.filter(
        (n: NotificationProps) => !n.read
    ).length;

    const todayNotifications = notifications.filter((n: NotificationProps) =>
        dayjs(n.createdAt).isSame(today, "day")
    );
    const previousNotifications = notifications.filter(
        (n: NotificationProps) => !dayjs(n.createdAt).isSame(today, "day")
    );

    const sections = [
        { title: t("notification.date.new"), data: todayNotifications },
        { title: t("notification.date.past"), data: previousNotifications },
    ].filter((section) => section.data.length > 0);

    return {
        notifications,
        sections,
        unreadCount,
        loading,
        hasMore,
        page,
        loadNotifications,
        handleNotificationClick,
        handleNavigateToProject,
        selectedStructure,
    };
};
